/**
 * @file authorizations.js
 * @description Single source of truth for BCBA / BCaBA case "Session Types /
 *   Authorizations" — the billable lines that drive the contract. Each line
 *   carries its official CPT code (suffix shown to agencies AND therapists).
 *
 *   - Behavior Treatment → 97155 (adaptive behavior treatment WITH protocol
 *     modification, QHP/BCBA) — entered as hours/week.
 *   - Parent Training → 97156 (family adaptive behavior treatment guidance) —
 *     entered as hours/week.
 *   - Assessment → no CPT suffix (per product decision) — entered as a ONE-TIME
 *     total for the case, NOT rolled into the weekly total.
 *
 *   Codes are static product config (not stored per case); they're applied as a
 *   display suffix wherever an authorization is shown.
 *
 * @module lib/authorizations
 */

// Weekly authorization line keys — these are the keys stored in
// service_time.sessionHours and the only ones summed into the weekly `hours`.
export const WEEKLY_AUTH_KEYS = ["Behavior Treatment", "Parent Training"];

// CPT code per authorization key. Assessment intentionally has no code.
export const AUTH_CPT = {
  "Behavior Treatment": "97155",
  "Parent Training":    "97156",
};

// ── Clinician-facing display layer (render-only; stored keys never change) ────
// Canonical KEY → display NAME. Only "Behavior Treatment" is relabeled: CPT 97155
// is adaptive behavior treatment WITH protocol modification (the BCBA/QHP code),
// which reads backwards as plain "Behavior Treatment" next to the RBT's 97153.
export const SERVICE_DISPLAY = {
  "Behavior Treatment": "Behavior Treatment w/ Protocol Mod.",
};

// CPT code by canonical service key — the render layer's source of truth for the
// "· CODE" suffix. Superset of AUTH_CPT: adds the RBT direct-care code (97153) and
// the other codes carried by the tracker service catalog, so every surface can
// show the code the way the case authorizations already do.
export const SERVICE_CPT = {
  "Direct Therapy":        "97153",
  "Group Therapy":         "97154",
  "Supporting Assessment": "97152",
  "Behavior Treatment":    "97155",
  "Initial Assessment":    "97151",
  "Parent Training":       "97156",
  "Group Parent Training": "97157",
};

/** Canonical key → clinician-facing display name (unchanged when no override). */
export function serviceDisplayName(key) {
  return SERVICE_DISPLAY[key] || key;
}

/**
 * Clinician-facing label: display name + " · CPT" suffix when a code is known.
 * Pass an explicit cpt (e.g. a tracker row's cpt_code) to override the lookup.
 * e.g. serviceLabel("Behavior Treatment") → "Behavior Treatment w/ Protocol Mod. · 97155".
 */
export function serviceLabel(key, cpt) {
  const code = cpt || SERVICE_CPT[key] || null;
  const name = serviceDisplayName(key);
  return code ? `${name} · ${code}` : name;
}

// Full ordered config the posting form renders (weekly rows + the one-time
// Assessment total row).
export const BCBA_AUTHORIZATIONS = [
  { key: "Behavior Treatment", code: "97155", cadence: "weekly" },
  { key: "Parent Training",    code: "97156", cadence: "weekly" },
  { key: "Assessment",         code: null,    cadence: "total"  },
];

/**
 * Display label with the CPT suffix appended when one exists.
 * e.g. "Behavior Treatment" → "Behavior Treatment · 97155"; "Assessment" → "Assessment".
 * @param {string} key
 * @returns {string}
 */
export function authLabel(key) {
  return serviceLabel(key, AUTH_CPT[key]);
}

/**
 * Sum only the weekly authorization hours from a sessionHours map — used so the
 * weekly total never includes the one-time Assessment or any legacy session-type
 * keys left over from before this model.
 * @param {Record<string, number|string>} sessionHours
 * @returns {number}
 */
export function sumWeeklyHours(sessionHours = {}) {
  return WEEKLY_AUTH_KEYS.reduce((sum, k) => sum + (parseFloat(sessionHours?.[k]) || 0), 0);
}
